import { useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { CheckCircle2, Loader2, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-field bg-white text-sm text-ink focus:outline-none focus:border-forest transition-colors";
const labelCls = "block text-xs font-monolbl uppercase tracking-wider text-ink mb-2";

export const EnquiryForm = ({ applicantType, setApplicantType, cohorts = [], cohort, setCohort }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, applicant_type: applicantType, cohort });
      setSent(true);
      toast.success("Enquiry sent — Elisabete will reply with upcoming cohort dates.");
    } catch (err) {
      toast.error("Could not send your enquiry. Please try again or call +44 7479 815308.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enquire" data-testid="enquiry-section" className="py-24 px-6 md:px-12 max-w-4xl mx-auto">
      <div className="bg-linen border border-cream rounded-3xl p-8 md:p-12 shadow-xl">
        <div className="text-center max-w-lg mx-auto mb-10">
          <span className="font-monolbl text-xs uppercase tracking-[0.2em] text-forest font-semibold">
            Reserve Your Spot
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-ink mt-2 mb-3">
            Apply for the Next Intake
          </h2>
          <p className="text-sm text-moss">
            Intimate cohort sizes for high-touch supervision. Send your details to receive
            upcoming cohort dates and syllabus.
          </p>
        </div>

        {sent ? (
          <div data-testid="enquiry-success-message" className="text-center py-10">
            <CheckCircle2 className="w-12 h-12 text-forest mx-auto mb-4" />
            <h3 className="font-display text-2xl text-ink mb-2">Enquiry received.</h3>
            <p className="text-sm text-moss max-w-md mx-auto mb-6">
              Thank you, {form.name.split(" ")[0]}. Elisabete will personally reply to{" "}
              <span className="text-ink font-medium">{form.email}</span> with upcoming cohort
              dates and the full syllabus.
            </p>
            <button
              data-testid="enquiry-send-another-button"
              onClick={() => {
                setSent(false);
                setForm({ name: "", email: "", phone: "", message: "" });
              }}
              className="px-6 py-3 rounded-full border border-forest/30 text-forest text-xs font-semibold uppercase tracking-widest hover:border-forest transition-colors"
            >
              Send Another Enquiry
            </button>
          </div>
        ) : (
          <form data-testid="enquiry-form" className="space-y-6" onSubmit={submit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="enquiry-name" className={labelCls}>
                  Full Name
                </label>
                <input
                  id="enquiry-name"
                  data-testid="enquiry-name-input"
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Sarah Jenkins"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="enquiry-email" className={labelCls}>
                  Email Address
                </label>
                <input
                  id="enquiry-email"
                  data-testid="enquiry-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  placeholder="sarah@wellness.co.uk"
                  className={inputCls}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <label htmlFor="enquiry-type" className={labelCls}>
                  I am applying as
                </label>
                <select
                  id="enquiry-type"
                  data-testid="enquiry-type-select"
                  value={applicantType}
                  onChange={(e) => setApplicantType(e.target.value)}
                  className={inputCls}
                >
                  <option value="individual">Individual Therapist (£800)</option>
                  <option value="salon">Salon / Clinic (£3,000)</option>
                </select>
              </div>
              <div>
                <label htmlFor="enquiry-cohort" className={labelCls}>
                  Preferred Intake
                </label>
                <select
                  id="enquiry-cohort"
                  data-testid="enquiry-cohort-select"
                  value={cohort}
                  onChange={(e) => setCohort(e.target.value)}
                  className={inputCls}
                >
                  {cohorts.length === 0 && <option value="">First available intake</option>}
                  {cohorts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="enquiry-phone" className={labelCls}>
                  Direct Phone
                </label>
                <input
                  id="enquiry-phone"
                  data-testid="enquiry-phone-input"
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+44 7000 000000"
                  className={inputCls}
                />
              </div>
            </div>

            <div>
              <label htmlFor="enquiry-message" className={labelCls}>
                Experience or Clinic Notes
              </label>
              <textarea
                id="enquiry-message"
                data-testid="enquiry-message-input"
                rows="3"
                value={form.message}
                onChange={update("message")}
                placeholder="Tell Elisabete about your current massage background..."
                className={inputCls}
              ></textarea>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row items-center gap-4">
              <button
                data-testid="enquiry-submit-button"
                type="submit"
                disabled={sending}
                className="w-full sm:w-auto flex-1 py-4 px-8 rounded-full bg-forest text-linen text-xs font-semibold uppercase tracking-widest hover:bg-ink transition-colors shadow-md disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                Submit Course Enquiry
              </button>
              <a
                data-testid="call-now-link"
                href="tel:+447479815308"
                className="w-full sm:w-auto px-6 py-4 rounded-full border border-forest/30 text-forest text-xs font-semibold uppercase tracking-widest text-center hover:border-forest transition-colors flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" />
                Call +44 7479 815308
              </a>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};
